import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, GLPGrid, PanelCard, Spinner, Typography } from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { useLocationScope } from '../lib/LocationScopeContext';
import { vendorApiClient, type ReportsOverview } from '../lib/vendorApiClient';
import { BORDER, GREEN_INK, MUTED, ORANGE_INK, PageTitle, describeProductError } from './products/parts';

const NAVY = '#343F5B';
const RED = '#C0322B';
const BLUE = '#2D63A7';

/** Vendor-owned operations reporting. Shopper and market analytics remain out of scope. */
export default function Reports() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId } = useVendor();
  const { scope } = useLocationScope();
  const [data, setData] = useState<ReportsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeVendorId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    vendorApiClient
      .getReportsOverview(activeVendorId, scope === 'all' ? undefined : scope)
      .then((result) => {
        if (alive) setData(result);
      })
      .catch((reason: Error) => {
        if (alive) setError(describeProductError(reason, t));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [activeVendorId, scope, t]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const scopeName = data.scope?.name ?? t('reports.allLocations');
  const locale = i18n.language.startsWith('es') ? 'es-US' : 'en-US';

  return (
    <Box data-testid="reports-overview" sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1120 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <PageTitle title={t('reports.title')} subtitle={t('reports.subtitle')} />
        <Chip
          label={t('reports.scope', { scope: scopeName })}
          size="small"
          backgroundColor="#EEF1F8"
          textColor={NAVY}
          fontSize={12}
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      <Alert severity="info">{t('reports.privacyNote')}</Alert>

      <GLPGrid container spacing={2}>
        <Metric accent={GREEN_INK} tint="#EEF7EF" label={t('reports.metrics.live')} value={data.listings.live} />
        <Metric accent={ORANGE_INK} tint="#FFF4E8" label={t('reports.metrics.onSale')} value={data.listings.onSale} />
        <Metric accent={BLUE} tint="#EEF5FC" label={t('reports.metrics.needsAnswer')} value={data.catalog.needsAnswer} />
        <Metric accent={RED} tint="#FDF2F1" label={t('reports.metrics.recalls')} value={data.recalls.active} />
      </GLPGrid>

      <GLPGrid container spacing={2}>
        <ReportCard
          title={t('reports.catalog.title')}
          subtitle={t('reports.catalog.subtitle')}
          accent={GREEN_INK}
          rows={[
            [t('reports.catalog.total'), data.catalog.total],
            [t('reports.catalog.approved'), data.catalog.approved],
            [t('reports.catalog.pending'), data.catalog.pendingReview],
            [t('reports.catalog.returned'), data.catalog.returned],
            [t('reports.catalog.withGlp'), data.catalog.withGlp],
            [t('reports.catalog.stuck'), data.catalog.stuck],
          ]}
          action={t('reports.actions.products')}
          onAction={() => navigate('/products')}
        />
        <ReportCard
          title={t('reports.listings.title')}
          subtitle={t('reports.listings.subtitle', { scope: scopeName })}
          accent={ORANGE_INK}
          rows={[
            [t('reports.listings.priced'), data.listings.priced],
            [t('reports.listings.live'), data.listings.live],
            [t('reports.listings.hidden'), data.listings.hidden],
            [t('reports.listings.outOfStock'), data.listings.outOfStock],
            [t('reports.listings.onSale'), data.listings.onSale],
          ]}
          action={t('reports.actions.promotions')}
          onAction={() => navigate('/promotions')}
        />
        <ReportCard
          title={t('reports.workflow.title')}
          subtitle={t('reports.workflow.subtitle')}
          accent={BLUE}
          rows={[
            [t('reports.workflow.needsAnswer'), data.catalog.needsAnswer],
            [t('reports.workflow.resolved30d'), data.workflow.questionsResolved30d],
            [t('reports.workflow.openChanges'), data.workflow.changeRequestsOpen],
            [t('reports.workflow.decided30d'), data.workflow.changeRequestsDecided30d],
          ]}
          action={t('reports.actions.questions')}
          onAction={() => navigate('/products?status=needs_answer')}
        />
        <ReportCard
          title={t('reports.prices.title')}
          subtitle={t('reports.prices.subtitle', { scope: scopeName })}
          accent={NAVY}
          rows={[
            [t('reports.prices.changes7d'), data.priceActivity.changes7d],
            [t('reports.prices.changes30d'), data.priceActivity.changes30d],
            [
              t('reports.prices.lastChanged'),
              data.priceActivity.lastChangedAt ? formatDateTime(data.priceActivity.lastChangedAt, locale) : t('reports.never'),
            ],
          ]}
          action={t('reports.actions.prices')}
          onAction={() => navigate('/products')}
        />
      </GLPGrid>

      <ActivityPanel
        title={t('reports.imports.title')}
        subtitle={t('reports.imports.subtitle')}
        empty={t('reports.imports.empty')}
        action={t('reports.actions.import')}
        onAction={() => navigate('/products/add/import')}
      >
        {data.imports.map((item) => (
          <ActivityRow
            key={item.batchId}
            title={item.originalFilename ?? t('reports.imports.unnamed')}
            meta={t('reports.imports.meta', {
              date: formatDateTime(item.createdAt, locale),
              rows: item.rowCount,
              committed: item.committedCount,
              questions: item.questionedCount,
              rejected: item.rejectedCount,
            })}
            status={statusLabel(item.status, t)}
            statusTone={statusTone(item.status)}
          />
        ))}
      </ActivityPanel>

      <ActivityPanel
        title={t('reports.feeds.title')}
        subtitle={t('reports.feeds.subtitle')}
        empty={t('reports.feeds.empty')}
      >
        {data.adapters.map((adapter) => (
          <ActivityRow
            key={adapter.adapterId}
            title={t('reports.feeds.adapter', { type: adapter.type })}
            meta={adapter.latestRun
              ? t('reports.feeds.meta', {
                  date: formatDateTime(adapter.latestRun.startedAt, locale),
                  added: adapter.latestRun.productsAdded,
                  updated: adapter.latestRun.productsUpdated,
                  removed: adapter.latestRun.productsRemoved,
                })
              : t('reports.feeds.neverRun')}
            status={statusLabel(adapter.status, t)}
            statusTone={adapter.lastRunSuccess === false ? 'error' : statusTone(adapter.status)}
          />
        ))}
      </ActivityPanel>

      <Typography sx={{ color: MUTED, fontSize: 12 }}>
        {t('reports.generated', { date: formatDateTime(data.generatedAt, locale) })}
      </Typography>
    </Box>
  );
}

function Metric({ accent, tint, label, value }: { accent: string; tint: string; label: string; value: number }) {
  return (
    <GLPGrid size={{ xs: 12, sm: 6, lg: 3 }}>
      <PanelCard as="div" accent={accent} sx={{ height: 'auto', bgcolor: tint, gap: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#1f2733', lineHeight: 1.1 }}>{value}</Typography>
      </PanelCard>
    </GLPGrid>
  );
}

function ReportCard({
  title,
  subtitle,
  accent,
  rows,
  action,
  onAction,
}: {
  title: string;
  subtitle: string;
  accent: string;
  rows: Array<[string, string | number]>;
  action: string;
  onAction: () => void;
}) {
  return (
    <GLPGrid size={{ xs: 12, md: 6 }}>
      <PanelCard as="section" accent={accent} sx={{ height: '100%', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: '#1f2733' }}>{title}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.25 }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, borderBottom: `1px solid ${BORDER}`, pb: 0.75 }}>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>{label}</Typography>
              <Typography sx={{ fontWeight: 700, color: '#1f2733', fontSize: 13, textAlign: 'right' }}>{value}</Typography>
            </Box>
          ))}
        </Box>
        <Button ariaLabel={action} buttonText={action} variant="text" onClick={onAction} sx={{ alignSelf: 'flex-start', mt: 'auto' }} />
      </PanelCard>
    </GLPGrid>
  );
}

function ActivityPanel({
  title,
  subtitle,
  empty,
  action,
  onAction,
  children,
}: {
  title: string;
  subtitle: string;
  empty: string;
  action?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = items.some(Boolean);
  return (
    <PanelCard as="section" accent={NAVY} sx={{ height: 'auto', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: '#1f2733' }}>{title}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.25 }}>{subtitle}</Typography>
        </Box>
        {action && onAction && <Button ariaLabel={action} buttonText={action} variant="outlined" onClick={onAction} />}
      </Box>
      {hasItems ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{children}</Box> : <Typography sx={{ color: MUTED }}>{empty}</Typography>}
    </PanelCard>
  );
}

function ActivityRow({
  title,
  meta,
  status,
  statusTone,
}: {
  title: string;
  meta: string;
  status: string;
  statusTone: 'success' | 'warning' | 'error' | 'neutral';
}) {
  const tones = {
    success: { bg: '#E5F4E7', text: GREEN_INK },
    warning: { bg: '#FFF0DD', text: ORANGE_INK },
    error: { bg: '#FBE5E3', text: RED },
    neutral: { bg: '#EEF1F8', text: NAVY },
  };
  const tone = tones[statusTone];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, borderTop: `1px solid ${BORDER}`, pt: 1.25 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: '#1f2733', overflowWrap: 'anywhere' }}>{title}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 12, mt: 0.25 }}>{meta}</Typography>
      </Box>
      <Chip label={status} size="small" backgroundColor={tone.bg} textColor={tone.text} fontSize={11} />
    </Box>
  );
}

function statusTone(status: string): 'success' | 'warning' | 'error' | 'neutral' {
  const key = status.toLowerCase();
  if (['complete', 'completed', 'active', 'ready', 'succeeded', 'success'].includes(key)) return 'success';
  if (['failed', 'error', 'rejected'].includes(key)) return 'error';
  if (['pending', 'processing', 'running', 'queued', 'needs_review'].includes(key)) return 'warning';
  return 'neutral';
}

function statusLabel(status: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  return t(`reports.status.${status.toLowerCase()}`, { defaultValue: status.replaceAll('_', ' ') });
}

function formatDateTime(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}
